import React, { useEffect, useReducer, useState } from 'react';
import { TimkenGreaseDetail } from '../../../components/bearing-search/types/types';
import {
	greaseResultColumnLabels,
	greaseResultColumns,
	text,
	leftAlignedHeaders, bearingSearchTableHeads
} from '../../data';
import TableLoader from '../TableLoader';
import greaseSelectionReducer, {
	initialGreaseSelectionResultState,
} from './reducer';
import { View } from '../../types/types';
import SearchPaginationLabel from '../../../components/bearing-search/components/SearchPaginationLabel';
import { generateId, shouldBeCenterAligned, getContactSiteUrl } from '../../utils/engineering-tools';
import {
	analyticsData,
	getGreaseImpressionData,
	trackGreaseListImpression,
} from '../../utils/analytics';
import { trackProductClick } from '../../../analytics/engineering-tools';
import SortButton from '../SortButton';

interface GreaseSelectionResultsProps {
	data: TimkenGreaseDetail[];
	loading?: boolean;
}

const GreaseSelectionResults: React.FC<GreaseSelectionResultsProps> = ( {
	data,
	loading,
} ) => {
	const [ sortedData, setSortedData ] = useState<TimkenGreaseDetail[]>( [] );
	const [ refinementState, dispatch ] = useReducer(
		greaseSelectionReducer,
		initialGreaseSelectionResultState,
	);

	useEffect( () => {
		setSortedData( data );
		trackGreaseListImpression( data, analyticsData.greaseSearch.name );
	}, [ data ] );

	useEffect( () => {
		const key = refinementState.sortKey;

		if ( ! key ) {
			setSortedData( data );
		} else {
			const sorted = [ ...data ];
			const direction = refinementState.sortDirection;

			setSortedData(
				sorted.sort( ( a: TimkenGreaseDetail, b: TimkenGreaseDetail ) => {
					const value1 = a[ key ] as string | number;
					const value2 = b[ key ] as string | number;

					if ( direction === 'desc' ) {
						return value1 < value2 ? 1 : -1;
					}

					return value1 > value2 ? 1 : -1;
				} ),
			);
		}
	}, [ refinementState.sortKey, refinementState.sortDirection ] );

	const handleProductClick = ( item: TimkenGreaseDetail, index: number ) => {
		const product = getGreaseImpressionData(
			item,
			index,
			analyticsData.greaseSearch.name,
		);

		trackProductClick( product );
	};

	return (
		<div className="t-engineering-tools__results">
			<h2>{text.results}</h2>

			<p className="t-engineering-tools__results-link d-none">
				<a href="#">{text.importantNote}</a>
			</p>

			{loading && <TableLoader />}

			{! loading && (
				<>
					{data.length === 0 && (
						<div className="t-engineering-tools__no-results">
							{text.noResults}
						</div>
					)}

					{data.length > 0 && (
						<>
							<div className="t-engineering-tools__view-bar">
								<SearchPaginationLabel
									current={1}
									total={data.length}
									pageSize={data.length}
								/>

								<div className="t-engineering-tools__view-toggle">
									<button className="a-icon-cta">
										<span
											className={
												refinementState.view === View.LIST
													? 'icon-list-fill'
													: 'icon-list-outline'
											}
											aria-label={text.list}
											onClick={() =>
												dispatch( { type: 'change_view', payload: View.LIST } )
											}
										></span>
									</button>

									<button className="a-icon-cta">
										<span
											className={
												refinementState.view === View.GRID
													? 'icon-grid-fill'
													: 'icon-grid-outline'
											}
											aria-label={text.grid}
											onClick={() =>
												dispatch( { type: 'change_view', payload: View.GRID } )
											}
										></span>
									</button>
								</div>
							</div>

							{refinementState.view === View.LIST && (
								<div className="t-engineering-tools__table">
									<div className="c-bearings-table">
										<table>
											<caption className="sr-only">{text.results}</caption>
											<thead>
												<tr>
													<th scope="col">
														<div>
															{text.grease}

															{data.length > 1 && (
																<SortButton
																	sortKey="greaseCode"
																	direction={
																		refinementState.sortKey === 'greaseCode'
																			? refinementState.sortDirection
																			: undefined
																	}
																	isActive={
																		refinementState.sortKey === 'greaseCode'
																	}
																	onSort={( newSortKey ) =>
																		dispatch( {
																			type: 'sort_by',
																			payload: newSortKey,
																		} )
																	}
																/>
															)}
														</div>
													</th>

													{greaseResultColumns.map( ( column ) => {
														const colKey = `grease_result_column_${ generateId(
															column,
														) }`;
														const label = greaseResultColumnLabels[ column ];

														return (
															<th scope="col" key={colKey} className={shouldBeCenterAligned(label, 'head', leftAlignedHeaders, null)}>
																<div>
																	{label}

																	{data.length > 1 && (
																		<SortButton
																			sortKey={column}
																			direction={
																				column === refinementState.sortKey
																					? refinementState.sortDirection
																					: undefined
																			}
																			isActive={
																				column === refinementState.sortKey
																			}
																			onSort={( newSortKey ) =>
																				dispatch( {
																					type: 'sort_by',
																					payload: newSortKey,
																				} )
																			}
																		/>
																	)}
																</div>
															</th>
														);
													} )}
												</tr>
											</thead>
											<tbody>
												{sortedData.map( ( grease, rowIndex ) => {
													const code = grease.greaseCode;
													const rowKey = `grease_result_row_${ generateId(
														code,
													) }`;
													const contactUsUrl = getContactSiteUrl();
													const detailsUrl = `/details/grease/${ code }`;
													const msdsUrl = grease.msdsUrl || contactUsUrl;
													const productDataUrl =
														grease.productDataUrl || contactUsUrl;
													const productDataLabel = grease.productDataUrl
														? text.view
														: text.contactTimken;

													return (
														<tr key={rowKey}>
															<td>
																<a
																	className="btn btn--tertiary"
																	target="_blank"
																	rel="noreferrer"
																	href={detailsUrl}
																	onClick={() =>
																		handleProductClick( grease, rowIndex )
																	}
																>
																	{code}
																</a>
															</td>

															{greaseResultColumns.map( ( column ) => {
																const colKey = `grease_result_data_${ generateId(
																	column,
																) }`;

																return <td key={colKey} className={shouldBeCenterAligned(greaseResultColumnLabels[ column ], 'head', leftAlignedHeaders, null)}>{grease[ column ]}</td>;
															} )}
														</tr>
													);
												} )}
											</tbody>
										</table>
									</div>
								</div>
							)}

							{refinementState.view === View.GRID && (
								<div className="c-bearings-grid">
									<ul className="c-bearings-grid__list">
										{sortedData.map( ( grease, rowIndex ) => {
											const code = grease.greaseCode;
											const rowKey = `grease_result_row_${ generateId( code ) }`;
											const contactUsUrl = getContactSiteUrl();
											const detailsUrl = `/details/grease/${ code }`;
											const msdsUrl = grease.msdsUrl || contactUsUrl;
											const productDataUrl =
												grease.productDataUrl || contactUsUrl;
											const productDataLabel = grease.productDataUrl
												? text.view
												: text.contactTimken;

											return (
												<li key={rowKey} className="c-bearings-grid__item">
													<div className="c-bearings-grid__head">
														<h5>{code}</h5>

														<a
															className="btn btn--tertiary"
															href={detailsUrl}
															target="_blank"
															rel="noreferrer"
															onClick={() =>
																handleProductClick( grease, rowIndex )
															}
														>
															{text.viewDetails}
														</a>
													</div>

													<div className="row">
														<div className="col-md-4">
															<table>
																<tbody>
																	<tr>
																		<th scope="row">
																			{
																				greaseResultColumnLabels[
																					greaseResultColumns[0]
																					]
																			}
																		</th>
																		<td>{grease[greaseResultColumns[0]]}</td>
																	</tr>
																	<tr>
																		<th scope="row">
																			{
																				greaseResultColumnLabels[
																					greaseResultColumns[1]
																					]
																			}
																		</th>
																		<td>{grease[greaseResultColumns[1]]}</td>
																	</tr>
																	<tr>
																		<th scope="row">
																			{
																				greaseResultColumnLabels[
																					greaseResultColumns[2]
																					]
																			}
																		</th>
																		<td>{grease[greaseResultColumns[2]]}</td>
																	</tr>
																</tbody>
															</table>
														</div>

														<div className="offset-md-1 col-md-4">
															<table>
																<tbody>
																{greaseResultColumns.map(
																	(column, colIndex) => {
																		if (colIndex < 3) {
																			return null;
																		}

																		const colKey = generateId(column);

																		return (
																			<tr key={colKey}>
																				<th scope="row">
																					{greaseResultColumnLabels[column]}
																				</th>
																				<td>{grease[column]}</td>
																			</tr>
																		);
																	},
																)}
																</tbody>
															</table>
														</div>
													</div>
												</li>
											);
										})}
									</ul>
								</div>
							)}
						</>
					)}
				</>
			)}
		</div>
	);
};

export default GreaseSelectionResults;
